import {useCallback, useMemo, useRef} from 'react'
import {SelectPanel, SkeletonBox} from '@primer/react'
import {CommentDiscussionIcon, PlusIcon} from '@primer/octicons-react'
import {ReposSelector} from '@github-ui/repos-selector'
import {sendEvent} from '@github-ui/hydro-analytics'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {buildNewRepositoryUrl, queryForRepos} from '@github-ui/copilot-chat/components/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import type {TaskComposerTargetSelection} from '@github-ui/copilot-shared-ui/utils/task-composer-target-selection'

import {EnvironmentPicker} from './EnvironmentPicker'
import {CLOUD_ENVIRONMENT_ID, type EnvironmentOption} from './environment-option'
import {RepoBranchPill} from './RepoBranchPill'
import {ScopePicker} from '../ScopePicker'
import {isRepoInScope, type AvailableOrganization, type TaskComposerScope} from '../../../types/task-composer-scope'
import styles from './EnvironmentStrip.module.css'

export interface EnvironmentStripProps {
  hideRepoSelector?: boolean
  initialRepoNWO?: string
  initialBranch?: string
  targetSelection: TaskComposerTargetSelection
  onTargetSelectionChange: (selection: TaskComposerTargetSelection) => void
  selectedBranch: string | undefined
  onBranchSelect: (branch: string | undefined) => void
  currentPrompt?: string
  /** Login of the current user, used to pre-fill the owner on the new repository URL. */
  currentUserLogin?: string
  /** Avatar URL for the current user, forwarded to the scope picker. */
  currentUserAvatarUrl?: string
  selectedEnvironment: EnvironmentOption | null
  onSelectEnvironment: (env: EnvironmentOption) => void
  showEnvironmentPicker?: boolean
  /** Forwarded to the environment picker; adds the managed-sandbox option. */
  includeSandboxOption?: boolean
  /** Enables the Sandbox-only repo-less Chat mode for callers that support it. */
  allowChat?: boolean
  branchSelectorDisabled?: boolean
  disabled?: boolean
  /** When true and organizations are present, renders the scope picker in the strip. */
  showScopePicker?: boolean
  /** Currently-selected scope. Required when `showScopePicker` is true. */
  selectedScope?: TaskComposerScope
  /** Called when the user picks a new scope. */
  onSelectScope?: (scope: TaskComposerScope) => void
  /** Organizations the user can scope to. */
  availableOrganizations?: AvailableOrganization[]
  /**
   * When present, filters the repo picker to repos owned by this login (plus
   * public repos) and surfaces a scope hint under the picker title.
   */
  scopeOwner?: string
}

export function EnvironmentStrip({
  hideRepoSelector,
  initialRepoNWO,
  initialBranch,
  targetSelection,
  onTargetSelectionChange,
  selectedBranch,
  onBranchSelect,
  currentPrompt,
  currentUserLogin,
  currentUserAvatarUrl,
  selectedEnvironment,
  onSelectEnvironment,
  showEnvironmentPicker = false,
  includeSandboxOption = false,
  allowChat = false,
  branchSelectorDisabled = false,
  disabled = false,
  showScopePicker = false,
  selectedScope,
  onSelectScope,
  availableOrganizations,
  scopeOwner,
}: EnvironmentStripProps) {
  const hasAutoSelectedRef = useRef(false)
  const selectedRepo = targetSelection.kind === 'repository' ? targetSelection.repository : undefined
  const canSelectChat = allowChat && selectedEnvironment?.kind === 'sandbox'

  const handleTargetSelection = useCallback(
    (selection: TaskComposerTargetSelection) => {
      hasAutoSelectedRef.current = true
      onTargetSelectionChange(selection)
      if (selection.kind === 'chat') {
        onBranchSelect(undefined)
      }
    },
    [onBranchSelect, onTargetSelectionChange],
  )

  const onLoad = useCallback(
    (repositories: AgentDropdownRepository[]) => {
      if (targetSelection.kind === 'unselected' && !hasAutoSelectedRef.current) {
        let initialRepo: AgentDropdownRepository | undefined
        if (initialRepoNWO) {
          initialRepo = repositories.find(repo => repo.nameWithOwner === initialRepoNWO)
          if (initialRepo && initialRepo.enabled) {
            onTargetSelectionChange({kind: 'repository', repository: initialRepo})
            onBranchSelect(initialBranch || initialRepo.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const loadedLastUsedRepo = repositories.find(
            repo => repo.nameWithOwner === `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`,
          )
          if (loadedLastUsedRepo && loadedLastUsedRepo.enabled) {
            onTargetSelectionChange({kind: 'repository', repository: loadedLastUsedRepo})
            onBranchSelect(loadedLastUsedRepo?.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const firstEnabledRepo = repositories.find(repo => repo.enabled)
        if (firstEnabledRepo) {
          onTargetSelectionChange({kind: 'repository', repository: firstEnabledRepo})
          onBranchSelect(firstEnabledRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        }
      }
    },
    [initialRepoNWO, initialBranch, onBranchSelect, onTargetSelectionChange, targetSelection.kind],
  )

  const repositoryLoader = useCallback(
    async (query: string, defaultOwner?: string) => {
      const effectiveOwner = scopeOwner ?? defaultOwner
      const scopedQuery = scopeOwner && !query.includes('/') ? `${scopeOwner}/${query}` : query
      const repos = await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO)
      if (!scopeOwner) return repos
      return repos.filter(repo => isRepoInScope(repo, scopeOwner))
    },
    [initialRepoNWO, scopeOwner],
  )

  const repoPickerSubtitle = scopeOwner
    ? `Repositories in @${scopeOwner} and public repositories`
    : 'Your repositories and public repositories'

  const newRepoUrl = useMemo(
    () => buildNewRepositoryUrl(currentUserLogin, currentPrompt),
    [currentUserLogin, currentPrompt],
  )

  const hasPrimaryRepo = !!selectedRepo

  const newRepoLink = (
    <SelectPanel.SecondaryActionLink
      href={newRepoUrl}
      leadingVisual={PlusIcon}
      onClick={() => {
        sendEvent('agent_sessions.task_view', {target: 'NEW_REPOSITORY_LINK'})
      }}
    >
      New repository
    </SelectPanel.SecondaryActionLink>
  )

  return (
    <div className={styles.strip}>
      {showEnvironmentPicker && (
        <EnvironmentPicker
          appearance="strip"
          selectedEnvironmentId={selectedEnvironment?.id ?? CLOUD_ENVIRONMENT_ID}
          onSelectEnvironment={onSelectEnvironment}
          includeSandboxOption={includeSandboxOption}
          announceUnavailable={false}
          disabled={disabled}
        />
      )}

      {showScopePicker &&
        selectedScope &&
        onSelectScope &&
        currentUserLogin &&
        availableOrganizations &&
        availableOrganizations.length > 0 && (
          <ScopePicker
            selectedScope={selectedScope}
            onSelectScope={onSelectScope}
            organizations={availableOrganizations}
            currentUserLogin={currentUserLogin}
            currentUserAvatarUrl={currentUserAvatarUrl}
            forceIconOnly
            disabled={disabled}
          />
        )}

      {!hideRepoSelector && !hasPrimaryRepo && (
        <ReposSelector<AgentDropdownRepository>
          repositoryLoader={repositoryLoader}
          selectionVariant="single"
          iconOnly={false}
          responsiveStyle="full"
          selectAllOption={canSelectChat}
          selectAllOptionPlacement="inline"
          selectAllOptionText="Chat"
          selectAllOptionLeadingVisual={CommentDiscussionIcon}
          selectAllOptionSelected={targetSelection.kind === 'chat'}
          buttonText={targetSelection.kind === 'chat' ? 'Chat' : 'Select repository'}
          onSelect={repo => {
            if (repo) {
              handleTargetSelection({kind: 'repository', repository: repo})
              onBranchSelect(repo.defaultBranch)
            } else if (canSelectChat) {
              handleTargetSelection({kind: 'chat'})
            }
          }}
          currentSelection={selectedRepo}
          width="medium"
          additionalButtonProps={{
            'aria-label': targetSelection.kind === 'chat' ? 'Mode: Chat' : 'Select repository',
            leadingVisual: targetSelection.kind === 'chat' ? CommentDiscussionIcon : undefined,
            className: styles.stripButton,
            variant: 'invisible',
            size: 'small',
            disabled,
          }}
          onLoad={onLoad}
          loadingElement={<SkeletonBox width="120px" height="24px" />}
          secondaryAction={newRepoLink}
          subtitle={repoPickerSubtitle}
        />
      )}

      {hasPrimaryRepo && (
        <RepoBranchPill
          pair={{id: 'primary', repo: selectedRepo, branch: selectedBranch || selectedRepo.defaultBranch}}
          onRepoChange={(_id, repo) => {
            handleTargetSelection({kind: 'repository', repository: repo})
            onBranchSelect(repo.defaultBranch)
          }}
          onBranchChange={(_id, branch) => onBranchSelect(branch)}
          onRemove={() => {
            handleTargetSelection({kind: 'unselected'})
          }}
          removable={false}
          hideRepo={hideRepoSelector}
          initialRepoNWO={initialRepoNWO}
          onLoad={onLoad}
          secondaryAction={newRepoLink}
          branchSelectorDisabled={branchSelectorDisabled}
          disabled={disabled}
          scopeOwner={scopeOwner}
          repoPickerSubtitle={repoPickerSubtitle}
          allowChat={canSelectChat}
          onChatSelect={() => handleTargetSelection({kind: 'chat'})}
        />
      )}
    </div>
  )
}
